import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/programs", label: "Programs" },
  { to: "/admissions", label: "Admissions" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <header className="fixed top-0 inset-x-0 z-50">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <nav
          data-testid="main-nav"
          className="mt-3 flex items-center justify-between rounded-full border border-amber-200/60 bg-cream/80 backdrop-blur-xl px-4 sm:px-6 py-3 shadow-[0_8px_30px_rgba(2,132,199,0.08)]"
        >
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-3" onClick={() => setOpen(false)}>
            <img
              src="/images/logo.png"
              alt="Blooming Chaitanya Academy logo"
              className="h-11 w-11 rounded-full object-contain bg-white ring-2 ring-brand-sun/60 shadow-[0_6px_16px_rgba(56,189,248,0.25)]"
            />
            <span className="leading-tight">
              <span className="block font-display font-semibold text-base sm:text-lg">Blooming Chaitanya</span>
              <span className="block text-[10px] uppercase tracking-[0.2em] text-slate-500">Academy · Jaipur</span>
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `rounded-full px-4 py-2 text-sm font-semibold transition-colors duration-300 ${
                    isActive ? "bg-brand-sky/15 text-brand-deepsky" : "text-slate-600 hover:text-brand-deepsky"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/admissions"
              data-testid="nav-enquire-btn"
              className="ml-2 rounded-full bg-brand-coral px-5 py-2.5 text-sm font-bold text-white shadow-[0_6px_20px_rgba(251,113,133,0.4)] transition-transform duration-300 hover:scale-105"
            >
              Enquire Now
            </Link>
          </div>

          <button
            data-testid="nav-menu-toggle"
            onClick={() => setOpen(!open)}
            className="md:hidden grid h-11 w-11 place-items-center rounded-full bg-brand-sky/15 text-brand-deepsky"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </nav>

        <AnimatePresence>
          {open && (
            <motion.div
              key={location.pathname}
              initial={{ opacity: 0, y: -12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.25 }}
              className="md:hidden mt-2 rounded-3xl border border-amber-200/60 bg-cream/95 backdrop-blur-xl p-3 shadow-xl"
              data-testid="nav-mobile-menu"
            >
              {LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `block rounded-2xl px-5 py-3.5 font-semibold ${
                      isActive ? "bg-brand-sky/15 text-brand-deepsky" : "text-slate-600"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <Link
                to="/admissions"
                onClick={() => setOpen(false)}
                data-testid="nav-mobile-enquire-btn"
                className="mt-2 block rounded-2xl bg-brand-coral px-5 py-3.5 text-center font-bold text-white"
              >
                Enquire Now
              </Link>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
};
